import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import {
  Box,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { memo, useState } from 'react';

import { Pagination, Voices } from 'shared/api';

import { AvatarLightbox } from './avatar-lightbox';
import { VoiceRow } from './voice-row';

type Column = {
  id: string;
  label: string | React.ReactNode;
  sortable: boolean;
  align?: 'left' | 'right' | 'center';
};

const columns: Column[] = [
  { id: 'expand', label: '', sortable: false, align: 'left' },
  { id: 'name', label: 'Name', sortable: true, align: 'left' },
  { id: 'countryCode', label: 'Country', sortable: true, align: 'left' },
  { id: 'languages', label: 'Languages', sortable: false, align: 'left' },
  { id: 'specialty', label: 'Specialty', sortable: false, align: 'left' },
  { id: 'bio', label: 'Bio', sortable: false, align: 'left' },
  { id: 'status', label: 'Status', sortable: false, align: 'left' },
  { id: 'isPublished', label: 'Published', sortable: false, align: 'center' },
  { id: 'notes', label: 'Notes', sortable: false, align: 'left' },
  { id: 'updatedAt', label: 'Updated', sortable: true, align: 'left' },
  { id: 'control', label: 'Control', sortable: false, align: 'center' },
];

type Props = {
  voices: Voices[] | undefined;
  pagination: Pagination | null;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  isLoading?: boolean;
  onPageChange: (newPage: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onSortChange: (sortBy: string) => void;
  onEdit?: (voice: Voices) => void;
  onDelete?: (voice: Voices) => void;
};

export const VoicesTable = memo(
  ({
    voices,
    pagination,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    isLoading = false,
    onPageChange,
    onRowsPerPageChange,
    onSortChange,
    onEdit,
    onDelete,
  }: Props) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedVoice, setSelectedVoice] = useState<Voices | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [lightboxVoice, setLightboxVoice] = useState<Voices | null>(null);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, voice: Voices) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setSelectedVoice(voice);
    };

    const handleMenuClose = () => {
      setAnchorEl(null);
      setSelectedVoice(null);
    };

    const handleEdit = () => {
      if (selectedVoice && onEdit) {
        onEdit(selectedVoice);
      }
      handleMenuClose();
    };

    const handleDelete = () => {
      if (selectedVoice && onDelete) {
        onDelete(selectedVoice);
      }
      handleMenuClose();
    };

    const handleChangePage = (event: unknown, newPage: number) => {
      setExpandedId(null);
      onPageChange(newPage + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
      setExpandedId(null);
      onRowsPerPageChange(parseInt(event.target.value, 10));
    };

    const handleSortRequest = (columnId: string) => {
      setExpandedId(null);
      onSortChange(columnId);
    };

    const handleToggleExpand = (id: string) => {
      setExpandedId((current) => (current === id ? null : id));
    };

    return (
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <Box sx={{ width: '100%', height: 4 }}>{isLoading && <LinearProgress />}</Box>

        <Box sx={{ opacity: isLoading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <TableContainer sx={{ maxHeight: 'calc(100vh - 305px)' }}>
            <Table stickyHeader aria-label="voices table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      aria-sort={
                        column.sortable && sortBy === column.id
                          ? sortOrder === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : undefined
                      }
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={sortBy === column.id ? sortOrder : 'asc'}
                          onClick={() => handleSortRequest(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {voices && voices.length > 0 ? (
                  voices.map((voice) => (
                    <VoiceRow
                      key={voice._id}
                      voice={voice}
                      columnCount={columns.length}
                      isExpanded={expandedId === voice._id}
                      onToggle={handleToggleExpand}
                      onMenuOpen={handleMenuOpen}
                      onAvatarOpen={setLightboxVoice}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      Авторы не найдены
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
          <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        </Menu>

        <AvatarLightbox voice={lightboxVoice} onClose={() => setLightboxVoice(null)} />

        {pagination && (
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={pagination.total}
            rowsPerPage={rowsPerPage}
            page={page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        )}
      </Paper>
    );
  },
);

VoicesTable.displayName = 'VoicesTable';
